/**
 * ExportPreview - Preview section for export modal with copy/download actions
 *
 * Displays the exported content preview with:
 * - Line count indicator
 * - Copy to clipboard button with success feedback
 * - Download file button
 * - Truncated preview for large exports
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { FaCheck, FaCopy, FaDownload } from 'react-icons/fa';

// ============================================================================
// Types
// ============================================================================

export interface ExportPreviewProps {
  /** The exported content to preview */
  content: string;
  /** Callback when copy button is clicked */
  onCopy: () => Promise<boolean>;
  /** Callback when download button is clicked */
  onDownload: () => void;
  /** Optional label text (default: "Preview") */
  label?: string;
  /** Maximum characters to show before truncating (default: 2000) */
  maxPreviewLength?: number;
  /** Maximum height of preview area (default: "max-h-64") */
  maxHeight?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function ExportPreview({
  content,
  onCopy,
  onDownload,
  label = 'Preview',
  maxPreviewLength = 2000,
  maxHeight = 'max-h-64',
}: ExportPreviewProps): ReactElement {
  const [copySuccess, setCopySuccess] = useState(false);

  const lineCount = content.split('\n').length;
  const isTruncated = content.length > maxPreviewLength;
  const previewText = isTruncated ? content.slice(0, maxPreviewLength) + '\n... (truncated)' : content;

  const handleCopy = useCallback(async () => {
    const success = await onCopy();
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [onCopy]);

  return (
    <div className="space-y-2">
      {/* Header with label and action buttons */}
      <div className="flex items-center justify-between">
        <label className="text-ash text-sm">
          {label} ({lineCount} {lineCount === 1 ? 'line' : 'lines'}){isTruncated && ' — preview truncated'}
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className={clsx(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              copySuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate text-silver hover:bg-storm',
            )}
          >
            {copySuccess ? (
              <>
                <FaCheck className="h-3 w-3" />
                Copied!
              </>
            ) : (
              <>
                <FaCopy className="h-3 w-3" />
                Copy to Clipboard
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onDownload}
            className="bg-slate text-silver hover:bg-storm flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
          >
            <FaDownload className="h-3 w-3" />
            Download
          </button>
        </div>
      </div>

      {/* Preview content */}
      <div className={clsx('border-storm bg-void overflow-auto rounded-lg border p-3', maxHeight)}>
        <pre className="text-silver font-mono text-xs whitespace-pre-wrap">{previewText || 'No content to preview'}</pre>
      </div>
    </div>
  );
}
